import React, { useEffect, useRef, useState } from 'react';
import { Field, SegToggle } from '../components/controls';
import { CROP_PAD, DETECTION, FAMILY_SIDES, SIDE, SIDE_LABEL, TEXT_DEBOUNCE_MS } from '../../constants';
import { useWeddingDay } from '../state/useWeddingDay';

const e = React.createElement;

// The detector reports each box as fractions of the image, so neither the
// overlay nor the tile needs the file's real pixel size: the percentages are
// the ratios.
/**
 * A mobile number as it can be dialled, taken while they type.
 *
 * Somebody rings this from a venue on the wedding day, so the field holds
 * digits and nothing else. A country code's plus is kept where they put one
 * in front; a phone's own keypad offers letters and brackets too, and the
 * server refuses all of it, so it is filtered here rather than letting them
 * fill the field and be told no afterwards.
 */
const digitsOnly = (value) => {
  const text = String(value || '').trim();
  return (text.startsWith('+') ? '+' : '') + text.replace(/\D/g, '');
};

const boxOf = (tag) => {
  const box = tag.bbox || tag.box || {};
  return {
    left: box.Left !== undefined ? box.Left : box.left || 0,
    top: box.Top !== undefined ? box.Top : box.top || 0,
    width: box.Width !== undefined ? box.Width : box.width || 0,
    height: box.Height !== undefined ? box.Height : box.height || 0,
  };
};

const boxStyle = (tag) => {
  const box = boxOf(tag);
  return {
    left: `${box.left * 100}%`,
    top: `${box.top * 100}%`,
    width: `${box.width * 100}%`,
    height: `${box.height * 100}%`,
  };
};

/**
 * One face as a window onto the photograph, while the photograph is still in
 * the browser. Nothing is fetched and nothing is cut: the boxes are ratios
 * of an image this page already has.
 */
const cropStyle = (tag, src) => {
  const box = boxOf(tag);
  const width = Math.min(1, box.width * CROP_PAD);
  const height = Math.min(1, box.height * CROP_PAD);
  const left = Math.max(0, Math.min(1 - width, box.left - (width - box.width) / 2));
  const top = Math.max(0, Math.min(1 - height, box.top - (height - box.height) / 2));
  return {
    backgroundImage: `url("${src}")`,
    backgroundSize: `${100 / width}% ${100 / height}%`,
    backgroundPosition: `${width >= 1 ? 50 : (left / (1 - width)) * 100}% ${
      height >= 1 ? 50 : (top / (1 - height)) * 100
    }%`,
  };
};

/**
 * How to draw one face.
 *
 * While the photograph is open in this session the tile is a window onto it.
 * After that the photograph is gone, so a face they picked is drawn from its
 * stored crop and a face they did not is a plain square: nothing was kept of
 * it but the box.
 */
const faceStyle = (tag, photoUrl) => {
  if (photoUrl) return cropStyle(tag, photoUrl);
  if (!tag.src) return {};
  return {
    backgroundImage: `url("${tag.src}")`,
    backgroundSize: 'cover',
    backgroundPosition: '50% 50%',
  };
};

// Detection runs here, on the picture the couple just picked, so this is a
// moment rather than a wait. It still has a state: the first photograph of
// the session also fetches the detector.
const isRunning = (panel) => panel.detection_status === DETECTION.RUNNING;

/** One face's relationship. Free text, so it is debounced like any other. */
const RelationshipField = ({ tag, disabled, onCommit }) => {
  const [value, setValue] = useState(tag.relationship || '');
  const timer = useRef(null);

  // The server is the source of truth: a tag that comes back changed, from a
  // poll or the other device, replaces what is on screen. Its own save comes
  // back trimmed, though, and taking that over the field took the space off
  // the end of "Elder " before "brother" could follow it.
  useEffect(() => {
    const saved = tag.relationship || '';
    setValue((typed) => (typed.trim() === saved ? typed : saved));
  }, [tag.relationship]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const schedule = (next) => {
    setValue(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(next), TEXT_DEBOUNCE_MS);
  };

  return e(Field, {
    label: 'Relationship',
    value,
    disabled,
    placeholder: 'For example, Elder brother',
    onChange: schedule,
    onBlur: () => {
      clearTimeout(timer.current);
      onCommit(value);
    },
  });
};

const FacePanel = ({ side, isPhone }) => {
  const { family, limits, sharedLocked, fieldErrors, uploadPhoto, removePhoto,
    setFace } = useWeddingDay();
  const panel = family[side] || {};
  const picker = useRef(null);
  // Raised when a tap would go past the cap. A refusal the couple has to
  // read and dismiss, rather than a tap that silently does nothing.
  const [capHit, setCapHit] = useState(false);
  // The face open at full size, by id, so one taken off while it is open
  // closes the view rather than showing a face no longer on the list.
  const [zoomId, setZoomId] = useState(null);
  useEffect(() => {
    if (zoomId === null) return undefined;
    const key = (ev) => { if (ev.key === 'Escape') setZoomId(null); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [zoomId]);
  const tags = panel.tags || [];
  const selected = tags.filter((t) => t.is_selected);
  const zoomTag = selected.find((t) => String(t.id) === zoomId) || null;
  const locked = sharedLocked;
  // A side carries as many photographs as they upload. Until the server has
  // answered the first one there is only the panel's own optimistic copy, so
  // that stands in as a single photograph.
  const photos = (panel.photos && panel.photos.length)
    ? panel.photos
    : (panel.photo_url
      ? [{
          photo_id: panel.photo_id,
          photo_url: panel.photo_url,
          detection_status: panel.detection_status,
          tags,
        }]
      : []);
  // The photographs this browser still holds. A photograph from an earlier
  // session is not one of them: it was never uploaded, so there is nothing
  // to draw, nothing to tap a face on, and no reason to take up a frame.
  // What it left behind is the faces the couple picked, which are stored as
  // crops and are shown below whether or not the photograph is still here.
  const live = photos.filter((photo) => photo.photo_url);
  // A face is drawn from the photograph it was found on. A side can hold
  // several, so the panel's own latest is the wrong one to reach for.
  const urlFor = (tag) => {
    const owner = photos.find(
      (p) => String(p.photo_id) === String(tag.photo_id)
    );
    return (owner && owner.photo_url) || '';
  };
  const running = photos.some((p) => isRunning(p)) || isRunning(panel);
  const failed = photos.some((p) => p.detection_status === DETECTION.FAILED);
  // The cap travels with the photo, so the payload's own number wins over the
  // form wide limit.
  const cap = panel.face_cap || limits.max_faces_per_side || 0;
  const atCap = cap > 0 && selected.length >= cap;
  const label = side === SIDE.BRIDE ? SIDE_LABEL[SIDE.BRIDE] : SIDE_LABEL[SIDE.GROOM];
  const error = fieldErrors[`family:${side}`];

  const toggle = (tag) => {
    if (locked) return;
    if (!tag.is_selected && atCap) {
      setCapHit(true);
      return;
    }
    // Deselecting drops the name with it: a face nobody picked has no
    // relationship to keep.
    setFace(side, tag.id, {
      is_selected: !tag.is_selected,
      ...(tag.is_selected ? { relationship: '' } : {}),
    });
  };

  return e('section', null, [
    e('div', { className: 'wd-card', key: 'photo' }, [
      e('span', { className: 'wd-flabel', key: 'l' }, `${label} family photograph`),
      e('input', {
        key: 'picker',
        ref: picker,
        type: 'file',
        accept: 'image/png,image/jpeg',
        multiple: true,
        style: { display: 'none' },
        onChange: (ev) => {
          // One at a time, each waiting for the last. Every photograph is
          // decoded full size to be scanned, and ten of them at once is
          // enough to take a phone's webview down.
          Array.from(ev.target.files || []).reduce(
            (prev, file) => prev.then(() => uploadPhoto(side, file)),
            Promise.resolve()
          );
          // Cleared so choosing the same file again still fires a change.
          ev.target.value = '';
        },
      }),
      live.length
        ? e(
            'div',
            { className: 'wd-photos', key: 'frames' },
            live.map((photo, i) =>
              e('div', { className: 'wd-photo', key: String(photo.photo_id || i) }, [
                photo.photo_url
                  ? e('img', {
                      key: 'img',
                      src: photo.photo_url,
                      alt: `${label} family photograph ${i + 1}`,
                    })
                  // The photograph itself was never uploaded, so once the
                  // page has been closed there is nothing left to show of
                  // it but what was found on it.
                  : e(
                      'span',
                      { className: 'wd-photogone', key: 'gone' },
                      `${photo.face_count || 0} face${
                        photo.face_count === 1 ? '' : 's'
                      } found. The photograph stayed on your device.`
                    ),
                isRunning(photo)
                  ? e(
                      'span',
                      { className: 'wd-detecting', key: 'busy' },
                      'Finding faces on your device.'
                    )
                  : null,
                // Takes this photograph off the side. Nothing is asked
                // first: the faces they picked stay, so there is nothing
                // to lose by it.
                locked || !photo.photo_id
                  ? null
                  : e(
                      'button',
                      {
                        key: 'rm',
                        type: 'button',
                        className: 'wd-photorm',
                        'aria-label': `Remove family photograph ${i + 1}`,
                        title: 'Remove this photograph',
                        onClick: () => removePhoto(side, photo.photo_id),
                      },
                      '\u2715'
                    ),
                e(
                  'div',
                  { className: 'wd-boxes', key: 'boxes' },
                  (photo.tags || []).map((tag) =>
                    e('button', {
                      key: String(tag.id),
                      type: 'button',
                      className: `wd-facebox${tag.is_selected ? ' is-on' : ''}`,
                      style: boxStyle(tag),
                      'aria-label': tag.is_selected
                        ? `Remove face ${tag.relationship || ''}`
                        : 'Add this face',
                      'aria-pressed': String(!!tag.is_selected),
                      disabled: locked,
                      onClick: () => toggle(tag),
                    })
                  )
                ),
              ])
            )
          )
        : e(
            'button',
            {
              key: 'upload',
              type: 'button',
              className: 'wd-upload',
              disabled: locked,
              onClick: () => picker.current && picker.current.click(),
            },
            [
              e('span', { className: 'wd-t', key: 't' }, 'Upload family photograph'),
              e('span', { className: 'wd-h', key: 'h' },
                'JPG or PNG. Add as many as you need.'),
            ]
          ),
      live.length && !locked
        ? e(
            'button',
            {
              key: 'replace',
              type: 'button',
              className: 'wd-btn wd-btn-ghost',
              style: { marginTop: 12 },
              onClick: () => picker.current && picker.current.click(),
            },
            'Add another photograph'
          )
        : null,
      failed
        ? e('div', { className: 'wd-warnbox', key: 'failed' },
            // Detection is done on the picture itself rather than against a
            // stored copy, so trying again means picking one again.
            'We could not read the faces in that photograph. '
            + 'Try another one.')
        : null,
      error ? e('p', { className: 'wd-fielderror', key: 'err' }, error) : null,
    ]),
    (live.length || selected.length) && !running
      ? e('div', { className: 'wd-card', key: 'faces' }, [
          e(
            'span',
            { className: 'wd-flabel', key: 'l' },
            // Two different numbers: how many faces the detector found, and
            // how many of them have been picked out against the cap. With no
            // photograph left on screen only the second one means anything:
            // the faces that were not picked cannot be tapped any more.
            // eslint-disable-next-line no-nested-ternary
            !live.length
              ? `${selected.length} of ${cap || selected.length} added`
              : cap
                ? `Faces found (${tags.length}) \u00b7 ${selected.length} of ${cap} added`
                : `Faces found (${tags.length})`
          ),
          e(
            'p',
            { className: 'wd-caption', key: 'hint', style: { margin: '0 0 10px' } },
            // eslint-disable-next-line no-nested-ternary
            !live.length
              ? 'These are the faces you added. The photographs they came from stayed on your device, so add a photograph again to pick more.'
              : tags.length
                ? 'Tap a face on any of your photographs to add them, then say who they are. Our photographers use this to find your immediate family on the day.'
                : 'No faces were detected yet. Try a clearer group photo, or add another one.'
          ),
          atCap
            ? e(
                'p',
                { className: 'wd-caption', key: 'cap' },
                'Limit reached. Remove a face to make room for another.'
              )
            : null,
          e(
            'div',
            { className: 'wd-facegrid', key: 'grid' },
            selected.map((tag) =>
              e('div', { className: 'wd-facecard', key: String(tag.id) }, [
                // A small circle is hard to be sure of, so a tap opens the
                // face at full size.
                e('button', {
                  key: 'crop',
                  type: 'button',
                  className: 'wd-facecrop is-on is-zoomable',
                  style: faceStyle(tag, urlFor(tag)),
                  'aria-label': `Enlarge ${tag.relationship || 'this face'}`,
                  onClick: () => setZoomId(String(tag.id)),
                }),
                e(RelationshipField, {
                  key: 'rel',
                  tag,
                  disabled: locked,
                  onCommit: (value) => {
                    const next = value.trim();
                    if ((tag.relationship || '') === next) return;
                    setFace(side, tag.id, { relationship: next });
                  },
                }),
                locked
                  ? null
                  : e(
                      'button',
                      {
                        key: 'rm',
                        type: 'button',
                        className: 'wd-rm',
                        'aria-label': `Remove ${tag.relationship || 'this face'}`,
                        onClick: () => toggle(tag),
                      },
                      // Two cards to a row leaves no width for the whole
                      // sentence, and wrapped it reads as a paragraph
                      // rather than a link.
                      isPhone ? 'Remove' : 'Remove this face'
                    ),
              ])
            )
          ),
        ])
      : null,
    zoomTag
      ? e(
          'div',
          { className: 'wd-scrim', key: 'zoom', onClick: () => setZoomId(null) },
          e('div', { className: 'wd-modal wd-facezoom', onClick: (ev) => ev.stopPropagation() }, [
            e('header', { key: 'head' }, e('h3', null, zoomTag.relationship || 'A face you added')),
            e(
              'div',
              { className: 'wd-scrollpane', key: 'body' },
              e('span', {
                className: 'wd-facezoom-img',
                style: faceStyle(zoomTag, urlFor(zoomTag)),
                role: 'img',
                'aria-label': zoomTag.relationship || 'A face you added',
              })
            ),
            e(
              'footer',
              { key: 'foot' },
              e(
                'div',
                { className: 'wd-acts' },
                e(
                  'button',
                  {
                    type: 'button',
                    className: 'wd-btn wd-btn-brand',
                    onClick: () => setZoomId(null),
                  },
                  'Close'
                )
              )
            ),
          ])
        )
      : null,
    capHit
      ? e(
          'div',
          { className: 'wd-scrim', key: 'caphit', onClick: () => setCapHit(false) },
          e('div', { className: 'wd-modal', onClick: (ev) => ev.stopPropagation() }, [
            e('header', { key: 'head' }, e('h3', null, 'That is as many as we can take')),
            e('div', { className: 'wd-scrollpane', key: 'body' }, [
              e(
                'p',
                { key: 'p' },
                `You have already added ${cap} faces on the ${label.toLowerCase()}. Remove one of them to make room, then pick the face you want instead.`
              ),
            ]),
            e(
              'footer',
              { key: 'foot' },
              e(
                'div',
                { className: 'wd-acts' },
                e(
                  'button',
                  {
                    type: 'button',
                    className: 'wd-btn wd-btn-brand',
                    onClick: () => setCapHit(false),
                  },
                  'Got it'
                )
              )
            ),
          ])
        )
      : null,
  ]);
};

/**
 * Who the crew calls on the day, for one side.
 *
 * The coordinator is that side's own point of contact; the backup is who to
 * try when neither coordinator answers, so it is asked of both sides too.
 */
const ContactPanel = ({ side }) => {
  const { contacts, sharedLocked, setContact } = useWeddingDay();
  const row = (kind) =>
    contacts.find((c) => c.side === side && c.kind === kind) || {};
  const coordinator = row('coordinator');
  const backup = row('backup');
  const label = side === SIDE.BRIDE ? SIDE_LABEL[SIDE.BRIDE] : SIDE_LABEL[SIDE.GROOM];

  return e('div', { className: 'wd-contacts' }, [
    e('div', { className: 'wd-card', key: 'coord' }, [
      e('span', { className: 'wd-flabel', key: 'h' }, `${label} coordinator`),
      e(
        'p',
        { className: 'wd-caption', key: 'c', style: { margin: '0 0 10px' } },
        'One point of contact from this side.'
      ),
      e('div', { className: 'wd-grid3', key: 'grid' }, [
        e(Field, {
          key: 'name',
          label: 'Name',
          value: coordinator.name || '',
          placeholder: 'Full name',
          disabled: sharedLocked,
          onChange: (v) => setContact(side, 'coordinator', { name: v }),
        }),
        e(Field, {
          key: 'phone',
          label: 'Mobile number',
          type: 'tel',
          value: coordinator.phone || '',
          placeholder: '+919876543210',
          disabled: sharedLocked,
          onChange: (v) => setContact(side, 'coordinator',
                                      { phone: digitsOnly(v) }),
        }),
        e(Field, {
          key: 'rel',
          label: 'Relationship',
          value: coordinator.relationship || '',
          placeholder: 'For example, uncle',
          disabled: sharedLocked,
          onChange: (v) => setContact(side, 'coordinator', { relationship: v }),
        }),
      ]),
    ]),
    e('div', { className: 'wd-card wd-backup', key: 'backup' }, [
      e('span', { className: 'wd-flabel', key: 'h' }, 'Backup contact'),
      e(
        'p',
        { className: 'wd-caption', key: 'c', style: { margin: '0 0 10px' } },
        'If we cannot reach the coordinator on the wedding day, who should we call?'
      ),
      e('div', { className: 'wd-grid2', key: 'grid' }, [
        e(Field, {
          key: 'name',
          label: 'Name',
          value: backup.name || '',
          placeholder: 'Full name',
          disabled: sharedLocked,
          onChange: (v) => setContact(side, 'backup', { name: v }),
        }),
        e(Field, {
          key: 'phone',
          label: 'Mobile number',
          type: 'tel',
          value: backup.phone || '',
          placeholder: '+919876543210',
          disabled: sharedLocked,
          onChange: (v) => setContact(side, 'backup',
                                      { phone: digitsOnly(v) }),
        }),
      ]),
    ]),
  ]);
};

const FamilySection = ({ section, isPhone }) => {
  const [side, setSide] = useState(SIDE.BRIDE);

  return e('div', null, [
    e(
      'p',
      { className: 'wd-intro', key: 'intro' },
      section.question ||
        'Uploading family photographs helps our photographers identify your immediate family throughout the celebrations.'
    ),
    e(SegToggle, {
      key: 'toggle',
      label: 'Filling in',
      value: side,
      options: FAMILY_SIDES,
      onPick: setSide,
    }),
    // One side at a time. Both panels side by side made the section as tall
    // as the taller photograph, so the shorter side stood in a hole.
    e(FacePanel, { key: `faces-${side}`, side, isPhone }),
    e(ContactPanel, { key: 'contacts', side }),
  ]);
};

export default FamilySection;
